import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Scale, Calculator, ShieldCheck, Banknote, Loader2, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { API } from '../lib/api';
import axios from 'axios';

const SERVICE_META = {
  legal: {
    label: 'Legal Advice',
    Icon: Scale,
    heading: 'Book Legal Consultation',
    tagline: 'Get expert legal advice on property registration, agreements, RERA, disputes and more.',
  },
  taxation: {
    label: 'Taxation Advice',
    Icon: Calculator,
    heading: 'Book CA / CS Consultation',
    tagline: 'Capital gains, home loan tax benefits, TDS on property, ITR filing & more.',
  },
  home_inspection: {
    label: 'Home Inspection',
    Icon: ShieldCheck,
    heading: 'Book Home Inspection',
    tagline: 'Professional pre-possession inspection with detailed report & thermal imaging.',
  },
  home_loan: {
    label: 'Home Loan',
    Icon: Banknote,
    heading: 'Apply for Home Loan',
    tagline: 'Compare & apply for home loans from top banks at the best rates.',
  },
};

const CITY_OPTIONS = ['Gurgaon', 'Delhi', 'Noida', 'Bangalore', 'Mumbai', 'Pune', 'Hyderabad', 'Goa', 'Other'];

const ServiceBookingModal = ({ open, onOpenChange, service }) => {
  const meta = SERVICE_META[service] || SERVICE_META.legal;
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) { setSuccess(false); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast({ title: 'Missing details', description: 'Please enter your name and phone number.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/service-requests`, { service, ...form });
      setSuccess(true);
      toast({ title: 'Request received!', description: 'Our team will contact you within 24 hours.' });
      setTimeout(() => {
        setForm({ name: '', phone: '', email: '', city: '', message: '' });
        onOpenChange(false);
      }, 1500);
    } catch (err) {
      toast({ title: 'Submission failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = meta.Icon;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" aria-describedby="service-desc">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#fef2f4] flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#9a1b2f]" />
            </div>
            <div>
              <DialogTitle className="text-[#9a1b2f]">{meta.heading}</DialogTitle>
              <DialogDescription id="service-desc" className="text-xs text-gray-500 mt-0.5">{meta.tagline}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="mt-4 font-bold text-gray-900">Request Submitted!</h3>
            <p className="text-sm text-gray-500 mt-1">Our expert will connect with you shortly.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="s_name" className="text-xs">Full Name *</Label>
              <Input id="s_name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" className="h-10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="s_phone" className="text-xs">Phone *</Label>
                <Input id="s_phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXXXX XXXXX" className="h-10" />
              </div>
              <div>
                <Label htmlFor="s_city" className="text-xs">City</Label>
                <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>
                    {CITY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="s_email" className="text-xs">Email (optional)</Label>
              <Input id="s_email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="h-10" />
            </div>
            <div>
              <Label htmlFor="s_message" className="text-xs">How can we help?</Label>
              <Textarea id="s_message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder={`Briefly describe your ${meta.label.toLowerCase()} requirement...`} />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
              <Button type="submit" className="bg-[#9a1b2f] hover:bg-[#7a1524]" disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Book Now
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ServiceBookingModal;
